/**
 * OnboardingModal - Premium welcome + activation for first-time free users.
 * Shows once per session via localStorage flag.
 * Free users with 0 remaining downloads see the upgrade-focused variant instead.
 */
import { useState } from "react";
import { X, ArrowRight, Camera, CheckCircle2, Zap } from "lucide-react";

const SESSION_KEY = "spp_welcome_seen";

export function OnboardingModal({
  user,
  remainingDownloads,
  onClose,
  onUpgradeClick,
  onStartExploring,
}) {
  const hasDownloads = remainingDownloads > 0;
  const alreadySeen = sessionStorage.getItem(SESSION_KEY) === "1";
  const [visible, setVisible] = useState(!alreadySeen && user?.plan !== "paid");

  const dismiss = () => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setVisible(false);
    if (onClose) onClose();
  };

  const handleStartExploring = () => {
    dismiss();
    if (onStartExploring) onStartExploring();
  };

  if (!visible) return null;

  // ── Exhausted downloads variant ───────────────────────
  if (!hasDownloads) {
    return (
      <div className="fixed inset-0 z-[90] flex items-center justify-center" data-testid="onboarding-modal">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={dismiss} />
        <div
          className="relative z-10 max-w-md w-full mx-4 rounded-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300"
          style={{
            background: "linear-gradient(165deg, #1c1917 0%, #292524 40%, #1c1917 100%)",
            boxShadow: "0 0 80px rgba(202,138,4,0.12), 0 25px 60px rgba(0,0,0,0.5)",
          }}
        >
          <div className="h-1" style={{ background: "linear-gradient(90deg, #92400e, #d97706, #f59e0b, #d97706, #92400e)" }} />
          <button onClick={dismiss} className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,0.08)" }} data-testid="onboarding-close-btn">
            <X className="h-4 w-4" style={{ color: "#8a7e6e" }} />
          </button>
          <div className="px-7 sm:px-9 pt-8 pb-6 text-center">
            <p className="text-[10px] font-medium tracking-[0.25em] uppercase mb-2" style={{ color: "#a8956e" }}>Real and Professional</p>
            <h2 className="text-lg sm:text-xl font-bold mb-1" style={{ color: "#e8dcc8", fontFamily: "Georgia, serif" }}>Menu Food Photos</h2>
            <div className="mx-auto my-3 w-16 h-px" style={{ background: "linear-gradient(90deg, transparent, #b8973a, transparent)" }} />
            <h3 className="text-lg font-bold" style={{ color: "#f5f0e8" }} data-testid="onboarding-title">Your Free Samples Are Done</h3>
            <p className="text-sm font-semibold leading-snug mt-4 mb-5" style={{ color: "#d6cfc3" }}>
              Fix Your Menu Like Big Brands<br />
              to get <span className="text-lg font-extrabold" style={{ color: "#f59e0b" }}>3X</span> More Online Orders
            </p>
            <div className="space-y-2.5 mb-5 text-left">
              {["15,000+ real, professionally shot food photos", null, "No photoshoot cost, no delays, instant upload", "Upgrade your entire menu in a few clicks"].map((text, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <svg className="h-4 w-4 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" strokeWidth={2.5} stroke="#b8973a"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                  <span className="text-[13px] leading-snug" style={{ color: "#c8bfb0" }}>
                    {text || (<>Pre-approved for <span style={{ color: "#E23744", fontWeight: 600 }}>Zomato</span> & <span style={{ color: "#FC8019", fontWeight: 600 }}>Swiggy</span></>)}
                  </span>
                </div>
              ))}
            </div>
            <button
              onClick={() => { dismiss(); if (onUpgradeClick) onUpgradeClick(); }}
              className="w-full h-12 flex items-center justify-center gap-2 rounded-xl text-sm font-bold"
              style={{ background: "linear-gradient(135deg, #92400e 0%, #d97706 30%, #f59e0b 50%, #d97706 70%, #92400e 100%)", color: "#1c1917", boxShadow: "0 0 24px rgba(245,158,11,0.25)" }}
              data-testid="onboarding-upgrade-btn"
            >
              Unlock Full Menu Access <ArrowRight className="h-4 w-4" />
            </button>
            <button onClick={dismiss} className="w-full text-center text-[12px] py-2.5 mt-2" style={{ color: "#6b6356" }} data-testid="onboarding-skip-btn">Browse Locked Images</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Premium welcome variant (has free downloads) ──────
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center" data-testid="onboarding-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={dismiss} />
      <div
        className="relative z-10 max-w-md w-full mx-4 rounded-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300"
        style={{
          background: "linear-gradient(165deg, #1c1917 0%, #292524 40%, #1c1917 100%)",
          boxShadow: "0 0 80px rgba(202,138,4,0.12), 0 25px 60px rgba(0,0,0,0.5)",
        }}
      >
        {/* Gold accent bar */}
        <div className="h-1" style={{ background: "linear-gradient(90deg, #92400e, #d97706, #f59e0b, #d97706, #92400e)" }} />

        {/* Close */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-white/10"
          style={{ background: "rgba(255,255,255,0.08)" }}
          data-testid="onboarding-close-btn"
        >
          <X className="h-4 w-4" style={{ color: "#8a7e6e" }} />
        </button>

        <div className="px-7 sm:px-9 pt-8 pb-7">
          {/* Icon */}
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center mb-6"
            style={{ background: "rgba(245,158,11,0.12)" }}
          >
            <Camera className="h-6 w-6" style={{ color: "#d97706" }} />
          </div>

          {/* Title */}
          <h2
            className="text-xl sm:text-2xl font-bold mb-2 tracking-tight"
            style={{ color: "#f5f0e8", fontFamily: "Georgia, serif" }}
            data-testid="onboarding-title"
          >
            Welcome to Studio Pass 2.0
          </h2>

          {/* Subtext */}
          <p className="text-sm leading-relaxed mb-6" style={{ color: "#b0a594" }} data-testid="onboarding-subtitle">
            Browse real, <span style={{ color: "#E23744", fontWeight: 600 }}>Zomato</span> & <span style={{ color: "#FC8019", fontWeight: 600 }}>Swiggy</span>-approved food photos
            and upgrade your menu in minutes.
          </p>

          {/* Divider */}
          <div className="w-full h-px mb-6" style={{ background: "linear-gradient(90deg, transparent, rgba(184,151,58,0.25), transparent)" }} />

          {/* Value bullets */}
          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-[18px] w-[18px] shrink-0 mt-0.5" style={{ color: "#b8973a" }} />
              <span className="text-[13px] leading-snug" style={{ color: "#d6cfc3" }}>
                Real food photos — no AI, no rejections
              </span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-[18px] w-[18px] shrink-0 mt-0.5" style={{ color: "#b8973a" }} />
              <span className="text-[13px] leading-snug" style={{ color: "#d6cfc3" }}>
                Ready for <span style={{ color: "#E23744", fontWeight: 600 }}>Zomato</span>, <span style={{ color: "#FC8019", fontWeight: 600 }}>Swiggy</span> & your menus
              </span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-[18px] w-[18px] shrink-0 mt-0.5" style={{ color: "#b8973a" }} />
              <span className="text-[13px] leading-snug" style={{ color: "#d6cfc3" }}>
                No photoshoot. No delays. Instant use
              </span>
            </div>
          </div>

          {/* Activation message */}
          <div
            className="flex items-start gap-2.5 rounded-lg px-4 py-3 mb-6"
            style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(184,151,58,0.15)" }}
            data-testid="onboarding-activation-msg"
          >
            <Zap className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "#f59e0b" }} />
            <p className="text-xs leading-relaxed" style={{ color: "#d6cfc3" }}>
              Try <span className="font-bold" style={{ color: "#f59e0b" }}>{remainingDownloads} sample download{remainingDownloads !== 1 ? "s" : ""}</span> before upgrading.
            </p>
          </div>

          {/* Primary CTA */}
          <button
            onClick={handleStartExploring}
            className="w-full h-12 flex items-center justify-center gap-2 rounded-xl text-sm font-bold transition-all duration-200 hover:brightness-110"
            style={{
              background: "linear-gradient(135deg, #92400e 0%, #d97706 30%, #f59e0b 50%, #d97706 70%, #92400e 100%)",
              color: "#1c1917",
              boxShadow: "0 0 24px rgba(245,158,11,0.25), 0 4px 12px rgba(0,0,0,0.3)",
            }}
            data-testid="onboarding-explore-btn"
          >
            Start Exploring Photos <ArrowRight className="h-4 w-4" />
          </button>

          {/* Skip */}
          <button
            onClick={dismiss}
            className="w-full py-2.5 mt-2 text-center text-[12px] transition-colors hover:text-white/60"
            style={{ color: "#6b6356" }}
            data-testid="onboarding-skip-btn"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
